import { useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useInView } from "@/core/hooks";
import { IcmAnimatedMoney } from "@/icm/common";
import {
  getTotalPayout,
  visibleTraceKeys,
} from "@/icm/icm-statement-page/utils";
import type { IcmLineItem } from "@/icm/types";
import { getTableMinWidth } from "@/icm/utils";
import {
  BusinessUnitCol,
  CustomerCol,
  JobCompletedCol,
  PayoutAmountCol,
  TraceCol,
} from "./cols";

interface IcmStatementRuleTableProps {
  ruleName: string;
  items: IcmLineItem[];
  selectedLineItemId: string | null;
  onSelectLineItem: (lineItem: IcmLineItem) => void;
}

export const IcmStatementRuleTable = ({
  ruleName,
  items,
  selectedLineItemId,
  onSelectLineItem,
}: IcmStatementRuleTableProps) => {
  const columns = useMemo(
    () => [
      CustomerCol(),
      BusinessUnitCol(),
      JobCompletedCol(),
      ...visibleTraceKeys(items).map(TraceCol),
      PayoutAmountCol(),
    ],
    [items],
  );

  const tableMinWidth = useMemo(() => getTableMinWidth(columns), [columns]);
  const totalPayout = useMemo(() => getTotalPayout(items), [items]);
  const [headerRef, isHeaderInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Stack gap="sm" fullWidth>
      <Stack
        ref={headerRef}
        direction="row"
        align="baseline"
        className="gap-2.5"
      >
        <Typography variant="subtitleW1" className="leading-none">
          {ruleName}
        </Typography>
        <Stack className="rounded-full bg-muted/60 px-2.5 py-1">
          <IcmAnimatedMoney
            value={isHeaderInView ? totalPayout : 0}
            className="items-baseline text-base font-semibold leading-none text-secondary-text"
            centsClassName="text-[9px] font-medium text-muted-foreground"
          />
        </Stack>
      </Stack>
      <Card className="w-full overflow-hidden">
        <SimpleTable
          data={items}
          columns={columns}
          columnPinning={{
            left: ["customer_name"],
            right: ["payout_amount"],
          }}
          onRowClick={onSelectLineItem}
          selectedRowId={selectedLineItemId}
          getRowVariant={(item) =>
            item.warnings?.length ? "warning" : undefined
          }
          scrollAreaHeightClassName="h-auto"
          scrollAreaClassName="pr-0"
          scrollContentStyle={{ minWidth: tableMinWidth }}
        />
      </Card>
    </Stack>
  );
};
